import { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { contextService, ApiError } from '@/services'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { useAppStore } from '@/stores/appStore'
import {
  DOMAIN_BY_KEY,
  isDomainKey,
  deriveCompleteness,
  type DomainKey,
} from '@/lib/domainSchema'
import { DomainHeader } from '@/components/domain-detail/DomainHeader'
import { TruthCard } from '@/components/domain-detail/TruthCard'
import { GapCard } from '@/components/overview/GapCard'
import type { ContextGap, GapDomain } from '@/types/api'

/**
 * Maps a `StructuredBusinessContext` key to the corresponding `GapDomain`
 * value the gap analyzer emits. The backend's gap.domain enum is
 * restricted to these four — the remaining four context keys (mission,
 * targetMarket, competition, fundraising) never carry gaps in v1, so the
 * page simply skips the gap fetch for them.
 */
const DOMAIN_KEY_TO_GAP_DOMAIN: Partial<Record<DomainKey, GapDomain>> = {
  businessModel: 'business_model',
  product: 'product',
  goToMarket: 'go_to_market',
  team: 'team',
}

/**
 * Phase 4 — single-domain drill-in. Renders the structured truth for one
 * domain (read-only) plus open gaps scoped to that domain. Inline edit is
 * deferred until commit 13.5 / post-bundle once Vidit clarifies the
 * `PATCH /context/source-of-truth` semantic; for now every truth row
 * carries a "read-only" affordance so the constraint is intentional.
 */
export function DomainDetailPage() {
  const { domainKey } = useParams<{ domainKey: string }>()
  const startupId = useAppStore((s) => s.startup?.id ?? null)

  const valid = isDomainKey(domainKey)
  const domain = valid ? DOMAIN_BY_KEY[domainKey] : null

  const [section, setSection] = useState<
    Record<string, unknown> | undefined
  >(undefined)
  const [gaps, setGaps] = useState<ContextGap[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    if (!startupId || !domain) return
    setLoading(true)
    setError(null)

    const gapDomain = DOMAIN_KEY_TO_GAP_DOMAIN[domain.key]

    // allSettled so a gap-only or sot-only failure doesn't blank the page —
    // past learning `promise-all-wrong-primitive`.
    const [sotR, gapR] = await Promise.allSettled([
      contextService.getSourceOfTruth(startupId, domain.key),
      gapDomain
        ? contextService.getGaps(startupId, {
            domain: gapDomain,
            status: 'open',
          })
        : Promise.resolve({ gaps: [] as ContextGap[] }),
    ])
    if (sotR.status === 'fulfilled') {
      const next = (sotR.value[domain.key] ?? undefined) as
        | Record<string, unknown>
        | undefined
      setSection(next)
    }
    if (gapR.status === 'fulfilled') {
      setGaps(gapR.value.gaps ?? [])
    }
    const failures = [sotR, gapR].filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    )
    const has401 = failures.some(
      (f) => f.reason instanceof ApiError && f.reason.status === 401
    )
    if (failures.length === 2 && !has401) {
      const first = failures[0].reason
      setError(first instanceof Error ? first.message : 'Failed to load domain.')
    }
    setLoading(false)
  }, [startupId, domain])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const debouncedRefetch = useDebouncedCallback(() => {
    void fetchAll()
  }, 250)
  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['gap', 'context'] },
    enabled: !!startupId && !!domain,
    onUpdate: debouncedRefetch,
  })

  if (!valid || !domain) return <DomainNotFound />

  const completeness = deriveCompleteness(section)

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <DomainHeader label={domain.label} completeness={completeness} />

      {error && (
        <div
          role="alert"
          className="mb-6 text-sm px-3 py-2 rounded-md border border-warning/30 bg-warning/10 text-text-primary"
        >
          {error}
        </div>
      )}

      <section className="flex flex-col gap-3 mb-8">
        <header className="flex items-end justify-between gap-3">
          <h2 className="text-lg font-display font-semibold text-text-primary">
            What we know
          </h2>
          {!loading && (
            <span className="text-xs text-text-tertiary">
              {section
                ? `${countFilledSubFields(section, domain.fields.length)} of ${domain.fields.length} fields`
                : 'No content yet'}
            </span>
          )}
        </header>
        <TruthCard domain={domain} section={section} />
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex items-end justify-between gap-3">
          <h2 className="text-lg font-display font-semibold text-text-primary">
            What we don’t know yet
          </h2>
          {gaps.length > 0 && (
            <span className="text-xs text-text-tertiary">
              {gaps.length} open gap{gaps.length === 1 ? '' : 's'}
            </span>
          )}
        </header>
        {gaps.length === 0 ? (
          <p className="text-sm text-text-secondary px-1">
            {DOMAIN_KEY_TO_GAP_DOMAIN[domain.key]
              ? 'No open gaps in this domain.'
              : 'Gaps in this domain aren’t tracked yet.'}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {gaps.map((g) => (
              <GapCard key={g.id} gap={g} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function DomainNotFound() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <Link
        to="/overview"
        className="inline-flex items-center gap-1.5 text-xs text-text-tertiary hover:text-text-primary transition-colors w-fit"
      >
        <ArrowLeft size={14} />
        Back to overview
      </Link>
      <div className="mt-8 rounded-lg border border-border-default bg-bg-primary px-6 py-10 text-center">
        <h2 className="text-base font-display font-semibold text-text-primary">
          Domain not found
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          That URL doesn’t match any of the eight domains. Head back to the
          overview to pick one.
        </p>
      </div>
    </div>
  )
}

function countFilledSubFields(
  section: Record<string, unknown>,
  total: number
): number {
  const filled = Object.values(section).filter((v) => {
    if (v == null) return false
    if (typeof v === 'string') return v.trim().length > 0
    if (Array.isArray(v)) return v.length > 0
    return true
  }).length
  return Math.min(filled, total)
}
